import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import * as authApi from '../../api/auth';
import type { ApiUser, ApiRol } from '../../api/auth';
import { onSesionExpirada } from '../../api/client';

// Mantenemos el alias UserRole para retrocompatibilidad con componentes existentes.
export type UserRole = ApiRol;

interface User {
  id: string;
  name: string;
  username: string;
  role: UserRole;
  branch: string;        // nombre legible de la sede o "Todas las sedes"
  branchCode?: string;   // código interno (ej. "BOG-PWY")
  branchId?: number;     // id en backend
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void; // se mantiene por compatibilidad
  hasPermission: (permission: string) => boolean;
  canViewModule: (module: string) => boolean;
  canAccessBranch: (branch: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// =============================================================
// Mapeo backend → frontend User
// =============================================================
function fromApi(u: ApiUser): User {
  return {
    id: String(u.id),
    name: u.nombre,
    username: u.username,
    role: u.rol,
    branch: u.sede_nombre || 'Todas las sedes',
    branchCode: u.sede_codigo || undefined,
    branchId: u.sede_id || undefined,
  };
}

// =============================================================
// Credenciales del Quick Login (solo desarrollo).
// Deben coincidir con apps/api/scripts/seed.js.
// =============================================================
export interface QuickLoginCred {
  username: string;
  password: string;
  label: string;
}

// Refactor jun-2026: reducimos a 6 roles base alineados con la matriz
// simplificada (Paola super-admin · Admin · Mesero · Cajero · Contador ·
// Desarrollador). Las variantes Usaquén y `cocina_*` se eliminan del
// quick-login y del submenu "Cambiar rol (dev)" — los usuarios siguen en
// la base de datos pero no aparecen en la UI de switch rápido.
// IMPORTANTE (ago-2026): estas son contraseñas de usuarios REALES del sistema
// (el mismo seed.js las crea en producción). Antes el objeto se evaluaba
// siempre, así que los literales viajaban dentro del bundle servido a
// producción: cualquiera que abriera las devtools tenía la clave del super
// admin.
//
// Ahora el objeto está detrás de `import.meta.env.DEV`, que Vite reemplaza por
// `false` al compilar para producción — el bundle de prod queda con `{}` y los
// literales desaparecen por eliminación de código muerto. En dev sigue
// funcionando igual que siempre.
//
// Se pueden sobreescribir por .env local (VITE_QL_<ROL>_USER / _PASS) para no
// tener ninguna contraseña en el repo. Pendiente aparte: rotar estas claves,
// porque ya estuvieron versionadas.
function credsDev(): Record<string, QuickLoginCred> {
  const env = import.meta.env as Record<string, string | undefined>;
  const c = (rol: string, userDef: string, passDef: string, label: string): QuickLoginCred => ({
    username: env[`VITE_QL_${rol.toUpperCase()}_USER`] || userDef,
    password: env[`VITE_QL_${rol.toUpperCase()}_PASS`] || passDef,
    label,
  });
  return {
    paola: c('paola', 'paola.novoa', 'salonika2026', 'Paola — Super admin'),
    admin: c('admin', 'admin.parkway', 'admin123', 'Admin'),
    mesero: c('mesero', 'mesero.parkway', 'mesero123', 'Mesero'),
    cajero: c('cajero', 'cajero.parkway', 'cajero123', 'Cajero'),
    contador: c('contador', 'contadora', 'contadora123', 'Contador'),
    desarrollador: c('desarrollador', 'desarrollador', 'desarrollador1234', 'Desarrollador'),
  };
}

export const QUICK_LOGIN_CREDS: Record<string, QuickLoginCred> =
  import.meta.env.DEV ? credsDev() : {};

// Compatibilidad: algunos componentes legacy importan USERS_DB. En producción
// queda vacío (QUICK_LOGIN_CREDS es {}), que es exactamente lo que debe pasar.
export const USERS_DB = Object.entries(QUICK_LOGIN_CREDS).map(([id, c]) => ({
  id,
  name: c.username,
  username: c.username,
  password: c.password,
  role: 'desarrollador' as UserRole,
  branch: 'Todas las sedes',
}));

// =============================================================
// MÓDULOS OCULTOS GLOBALMENTE — no se muestran a ningún rol.
// =============================================================
const HIDDEN_MODULES = new Set<string>([
  // (vacío) — el módulo de Horarios se eliminó por completo (jun-2026).
]);

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restaurar sesión al cargar la app.
  //
  // Sin red (modo contingencia, oct-2026): se entra con el último usuario que
  // el servidor confirmó y se vuelve a preguntar cada 15 s. Cuando el servidor
  // contesta, manda él: si la sesión ya no sirve, vuelve el login.
  useEffect(() => {
    let cancelled = false;
    let reintento: ReturnType<typeof setTimeout> | null = null;
    const verificar = () => {
      authApi.fetchMe()
        .then(u => { if (!cancelled) setUser(u ? fromApi(u) : null); })
        .catch(() => {
          if (cancelled) return;
          const guardado = authApi.usuarioGuardado();
          if (guardado) setUser(prev => prev ?? fromApi(guardado));
          reintento = setTimeout(verificar, 15_000);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    verificar();
    return () => { cancelled = true; if (reintento) clearTimeout(reintento); };
  }, []);

  // La sesión se venció mientras la app estaba abierta (TTL de 12 h).
  //
  // Sin esto, el cliente borraba el token y nadie se enteraba: la pantalla
  // seguía mostrando los datos que ya había cargado, y el siguiente clic
  // devolvía "Token faltante" — un mensaje del backend que no le dice nada a
  // nadie. Volver a null devuelve la pantalla de login, que es lo único que el
  // usuario puede hacer al respecto.
  useEffect(() => onSesionExpirada(() => setUser(null)), []);

  const login = async (username: string, password: string) => {
    const u = await authApi.login(username, password);
    setUser(fromApi(u));
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  // ============================================================
  // Matriz de visibilidad por rol — refactor jun-2026.
  //
  // Mapeo de los roles existentes en backend (ApiRol) a la matriz simple
  // que el usuario pidió:
  //   • gerente_administrativo (Paola) → SUPER ADMIN — todo menos debug/docs.
  //   • gerente_operativo, jefe_*      → ADMIN — operaciones + inventario,
  //                                      SIN contabilidad/reports/users/kpis/
  //                                      legal-entities.
  //   • cajera                         → CAJERO (vigente oct-2026) — todo lo
  //                                      del mesero + inventario (cargar; para
  //                                      reducir/desactivar pide aprobación),
  //                                      costos y gastos, activos, facturación
  //                                      (ver), terceros y SOLO el cierre
  //                                      diario de caja dentro de Reportes.
  //   • mesero                         → MESERO — orders/reservations/tables/menu.
  //   • cocina                         → conservar (orders+menu+inventory).
  //   • contador                       → conservar (contable).
  //   • desarrollador                  → TODO.
  //   • public_user                    → solo dashboard.
  //
  // El "marcar plato no disponible" del mesero es un toggle dentro del
  // módulo Carta y se autoriza por permiso `menu_toggle_availability`
  // (no requiere `publish_menu` que sí necesita crear/editar).
  // ============================================================
  const ROLE_MODULES: Record<UserRole, ReadonlySet<string>> = {
    desarrollador: new Set([
      'dashboard', 'kpis', 'orders', 'reservations', 'tables', 'menu',
      'inventory', 'purchases', 'contabilidad', 'billing', 'payments',
      'users', 'terceros', 'reports', 'params', 'legal-entities',
      'debug', 'technical-docs',
      // Módulos con flag (se encienden desde Features): jul-2026.
      'ratings', 'staff',
      'cortesias',
      // Features (flags + presupuesto IA + historial de escaneos) — SOLO dev.
      'features',
      // Activos y consumibles (dotación, jabón…) — mismo público que Gastos.
      'activos',
      // Notificaciones (alertas, matriz de roles, correos) — superadmin + dev.
      'notificaciones',
      // Domicilios MU: monitoreo del courier + pedir servicios (ago-2026).
      'envios-mu',
      // Combos (mig. 049): armar paquetes de platos y ponerles precio.
      // Va SÓLO a los tres roles que routes/combos.js deja gestionar
      // (mismo set que routes/menu.js). Mostrárselo a alguien cuyo guardado
      // el backend va a rechazar con 403 es peor que no mostrarlo.
      'combos',
      // Aprobaciones (oct-2026): solicitudes del cajero que el admin de sede
      // aprueba (reducir stock, desactivar item, borrar gasto).
      'aprobaciones',
      // Llaves de terceros (Bold, MU, Anthropic, correo, WhatsApp) por sede.
      // MISMO set de roles que ADMIN en credenciales/routes.js: mostrárselo a
      // alguien a quien el backend le va a responder 403 es peor que ocultarlo.
      'credenciales',
    ]),
    gerente_administrativo: new Set([
      // "Super-admin" (Paola) — todo MENOS debug y technical-docs.
      // Oct-2026: SIN 'orders' ni 'tables' (Pedidos y Mesas son de la
      // operación de sede) y es el ÚNICO, junto al dev, que ve 'kpis'.
      'dashboard', 'kpis', 'reservations', 'menu',
      'inventory', 'purchases', 'contabilidad', 'billing', 'payments',
      'users', 'terceros', 'reports', 'params', 'legal-entities',
      // Calificaciones: SOLO superadmin + desarrollador (pedido jul-2026).
      // Personal (turnos): el superadmin también lo gestiona.
      'ratings', 'staff',
      'cortesias',
      // Notificaciones y Llaves y credenciales: SOLO desarrollador (oct-2026).
      'activos',
      // 'envios-mu' fuera (oct-2026): Domicilios MU solo lo ve el desarrollador.
      'combos',
      'aprobaciones',
    ]),
    administrador: new Set([
      // ADMIN por sede — TODO de su sede (incl. contabilidad/reportes), pero
      // SIN crear usuarios (no incluye 'users'). Limitado a su sede vía
      // branch_restricted + canAccessBranch.
      //
      // Oct-2026: sin 'payments' (Pagos = solo superadmin + contador), ni
      // 'legal-entities' (Entidades Legales = solo superadmin + contador), ni
      // 'kpis' (Estadísticas = solo superadmin).
      'dashboard', 'orders', 'reservations', 'tables', 'menu',
      'inventory', 'purchases', 'contabilidad', 'billing',
      'terceros', 'reports', 'params',
      'staff', 'cortesias', 'activos',
      // 'envios-mu' fuera (oct-2026): Domicilios MU solo lo ve el desarrollador.
      // Aprueba lo que el cajero de SU sede pide (oct-2026).
      'aprobaciones',
    ]),
    // Legacy (gerente_operativo, jefes): oct-2026 pierden 'payments' (solo
    // superadmin + contador) y 'params' (solo admin + superadmin).
    gerente_operativo: new Set([
      // LEGACY — operaciones + inventario. SIN contabilidad/reports/users/
      // kpis/legal-entities.
      'dashboard', 'orders', 'reservations', 'tables', 'menu',
      'inventory', 'purchases', 'billing',
      'staff', 'combos',
    ]),
    jefe_operaciones: new Set([
      // Mismo set que admin operativo.
      'dashboard', 'orders', 'reservations', 'tables', 'menu',
      'inventory', 'purchases', 'billing',
      'staff',
    ]),
    jefe_administrativo: new Set([
      // Mismo set que admin operativo.
      'dashboard', 'orders', 'reservations', 'tables', 'menu',
      'inventory', 'purchases', 'billing',
      'staff',
    ]),
    cajera: new Set([
      // CAJERO (oct-2026) = todo lo del mesero +
      'orders', 'reservations', 'tables', 'menu',
      // carga inventario (reducir/desactivar va por Aprobaciones),
      'inventory',
      // mete costos y gastos (borrar un gasto va por Aprobaciones),
      'purchases',
      // activos y consumibles,
      'activos',
      // ve la facturación y los terceros (clientes de factura electrónica),
      'billing', 'terceros',
      // ve SOLO el cierre diario de caja (reports.tsx mira el rol),
      'reports',
      // y el estado de sus solicitudes.
      'aprobaciones',
    ]),
    mesero: new Set([
      // Mesero estricto — solo operación de salón.
      'orders', 'reservations', 'tables', 'menu',
    ]),
    cocina: new Set([
      'orders', 'menu', 'inventory',
    ]),
    contador: new Set([
      'dashboard', 'purchases', 'contabilidad', 'reports', 'billing',
      'payments', 'legal-entities', 'terceros', 'activos',
      // Cortesías: valor regalado = dato contable.
      'cortesias',
    ]),
    public_user: new Set([
      'dashboard',
    ]),
  };

  // Permisos por rol — controlan acciones DENTRO de los módulos
  // (ej. crear/editar plato, ajustar stock, etc.). `all` es comodín.
  const ROLE_PERMISSIONS: Record<UserRole, ReadonlySet<string>> = {
    desarrollador:          new Set(['all', 'view_kpis']),
    gerente_administrativo: new Set(['all', 'publish_menu', 'manage_staff', 'manage_users', 'change_passwords', 'accounting_full', 'view_kpis', 'menu_toggle_availability', 'inventory_adjust']),
    // ADMIN por sede: todo de su sede salvo crear usuarios. Sin 'all' ni
    // 'manage_users'/'change_passwords' para que NO pueda crear/gestionar
    // usuarios (eso es solo super-admin/desarrollador).
    // Oct-2026: sin 'view_kpis' (Estadísticas = solo superadmin).
    administrador:          new Set(['publish_menu', 'manage_staff', 'accounting_full', 'menu_toggle_availability', 'inventory_adjust', 'inventory_manage_items', 'operations', 'orders', 'tables', 'reservations', 'branch_restricted']),
    // `manage_users` SOLO lo tienen super-admin (Paola = gerente_administrativo)
    // y desarrollador.
    //
    // `publish_menu` lo tiene TAMBIÉN el administrador: el admin de sede crea y
    // edita platos (confirmado sep-2026). Este comentario decía lo contrario y
    // contradecía a la línea de arriba, que ya le daba el permiso; el backend
    // sí le hacía caso al comentario y devolvía 403 al guardar. Arreglado en
    // routes/menu.js (MANAGE_MENU_ROLES).
    //
    // Los jefes y el gerente_operativo (legacy) siguen pudiendo mover
    // inventario y togglear disponibilidad, pero NO crear platos ni usuarios.
    gerente_operativo:      new Set(['inventory_adjust', 'menu_toggle_availability', 'operations']),
    jefe_operaciones:       new Set(['operations', 'inventory_adjust', 'menu_toggle_availability', 'manage_staff']),
    jefe_administrativo:    new Set(['operations', 'inventory_adjust', 'menu_toggle_availability', 'manage_staff', 'accounting_full']),
    // Cajero: lo del mesero + crear/editar items y ajustar stock. Reducir
    // stock o desactivar un item NO se ejecuta: el backend lo convierte en una
    // solicitud de Aprobaciones (apps/api/src/aprobaciones).
    //
    // OJO: las RESTRICCIONES del cajero (solo cierre en Reportes, pedir
    // aprobación) NO van como permiso acá: hasPermission() trata 'all' como
    // comodín, así que un permiso "restrictivo" se le prendería también al
    // superadmin y al dev. Se miran por rol (user.role === 'cajera').
    cajera:                 new Set(['orders', 'tables', 'reservations', 'menu_toggle_availability', 'branch_restricted', 'inventory_adjust', 'inventory_manage_items']),
    mesero:                 new Set(['orders', 'tables', 'reservations', 'menu_toggle_availability', 'branch_restricted', 'mesero_role']),
    cocina:                 new Set(['orders', 'inventory_adjust', 'menu_toggle_availability', 'branch_restricted', 'cocina_role']),
    contador:               new Set(['accounting_full', 'contador_role']),
    public_user:            new Set(['public_dashboard']),
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    const perms = ROLE_PERMISSIONS[user.role];
    return !!perms && (perms.has('all') || perms.has(permission));
  };

  const canViewModule = (module: string): boolean => {
    if (!user) return false;
    // Módulos ocultos globalmente (HIDDEN_MODULES) — solo desarrollador.
    if (HIDDEN_MODULES.has(module) && user.role !== 'desarrollador') return false;
    const modules = ROLE_MODULES[user.role];
    return !!modules && modules.has(module);
  };

  const canAccessBranch = (branch: string): boolean => {
    if (!user) return false;
    if (user.branch === 'Todas las sedes') return true;
    return user.branch === branch;
  };

  return (
    <UserContext.Provider value={{ user, loading, login, logout, setUser, hasPermission, canViewModule, canAccessBranch }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
